import React, { useState, useEffect, useCallback } from 'react';
import { Dropdown } from "@voicecare.ai/ui-library";
import callAutomationService from '../../services/callAutomationService';

const RenderingProviderSelection = ({
  value = '',
  onChange,
  placeholder = "Select Rendering Provider NPI",
  facilityId, // ID of the selected facility (for exact matching)
  facilityNPI, // NPI of the selected facility (fallback)
  departmentName, // Name of the selected department to further filter providers
  themeMode = 'dark',
  currentTheme = {},
  style = {},
  disabled = false
}) => {
  const [providers, setProviders] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [filteredProviders, setFilteredProviders] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // Load data (providers, facilities, departments)
  const loadData = useCallback(async () => {
    if (providers.length === 0 || facilities.length === 0 || departments.length === 0) {
      setIsLoading(true);
      try {
        const [providersData, facilitiesData, departmentsData] = await Promise.all([
          callAutomationService.getRenderingProviders(),
          callAutomationService.getFacilities(),
          callAutomationService.getDepartments()
        ]);
        setProviders(providersData);
        setFacilities(facilitiesData);
        setDepartments(departmentsData);
        console.log('👨‍⚕️ Loaded providers data:', providersData.length, 'providers');
        console.log('🏢 Loaded facilities data:', facilitiesData.length, 'facilities');
        console.log('📚 Loaded departments data:', departmentsData.length, 'departments');
      } catch (error) {
        console.error('❌ Error loading provider/facility/department data:', error);
      } finally {
        setIsLoading(false);
      }
    }
  }, [providers.length, facilities.length, departments.length]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Filter providers by facility and department
  const filterProvidersByFacility = useCallback(() => {
    let filtered = providers;

    // Prioritize facilityId for exact matching if available
    if (facilityId) {
      // If departmentName is also provided, filter by both facility and department
      if (departmentName && departments.length > 0) {
        // Find departments that match the department name AND belong to this facility
        const matchingDepartments = departments.filter(d => 
          d.name === departmentName && d.facilityId === facilityId
        );
        
        if (matchingDepartments.length > 0) {
          // Filter providers who have affiliations to this specific facility-department combination
          filtered = providers.filter(provider => 
            provider.affiliations?.some(affiliation => 
              matchingDepartments.some(dept => 
                affiliation.facilityId === facilityId && 
                affiliation.departmentId === dept.id
              )
            )
          );
          console.log(`🏢 Filtered ${filtered.length} providers for facility ID: ${facilityId} and department: ${departmentName} (exact match)`);
        } else {
          // Fallback: department not found, filter by facility only
          filtered = providers.filter(provider => 
            provider.affiliations?.some(affiliation => 
              affiliation.facilityId === facilityId
            )
          );
          console.log(`🔍 Department '${departmentName}' not found, filtered ${filtered.length} providers for facility ID: ${facilityId} only`);
        }
      } else {
        // No department, filter by facility ID only
        filtered = providers.filter(provider => 
          provider.affiliations?.some(affiliation => 
            affiliation.facilityId === facilityId
          )
        );
        console.log(`🔍 Filtered ${filtered.length} providers for facility ID: ${facilityId} (exact match)`);
      }
    }
    // Fallback: If departmentName is provided (and we have a facility NPI), filter by department affiliations
    else if (departmentName && facilityNPI && facilities.length > 0 && departments.length > 0) {
      // Find facilities by matching the groupNPI - handle multiple facilities with same NPI
      const facilitiesWithNPI = facilities.filter(f => f.value === facilityNPI);
      
      if (facilitiesWithNPI.length > 0) {
        // Find departments that match the department name AND belong to any of the matching facilities
        const matchingDepartments = departments.filter(d => 
          d.name === departmentName && facilitiesWithNPI.some(f => f.id === d.facilityId)
        );
        
        if (matchingDepartments.length > 0) {
          // Filter providers who have affiliations to any of these specific facility-department combinations
          filtered = providers.filter(provider => 
            provider.affiliations?.some(affiliation => 
              matchingDepartments.some(dept => 
                affiliation.facilityId === dept.facilityId && 
                affiliation.departmentId === dept.id
              )
            )
          );
          console.log(`🏢 Filtered ${filtered.length} providers for facility NPI: ${facilityNPI} and department: ${departmentName}`);
        } else {
          // Fallback: department not found, filter by facilities only
          const facilityIds = facilitiesWithNPI.map(f => f.id);
          filtered = providers.filter(provider => 
            provider.affiliations?.some(affiliation => 
              facilityIds.includes(affiliation.facilityId)
            )
          );
          console.log(`🔍 Department '${departmentName}' not found, filtered ${filtered.length} providers for facilities only`);
        }
      }
    }
    // If only facilityNPI is provided (no department, no facilityId), filter by facility affiliations
    else if (facilityNPI && facilities.length > 0) {
      // Find facilities by matching the groupNPI - handle multiple facilities with same NPI
      const facilitiesWithNPI = facilities.filter(f => f.value === facilityNPI);
      
      if (facilitiesWithNPI.length === 1) {
        // Single facility with this NPI
        const facility = facilitiesWithNPI[0];
        filtered = providers.filter(provider => 
          provider.affiliations?.some(affiliation => 
            affiliation.facilityId === facility.id
          )
        );
        console.log(`🔍 Filtered ${filtered.length} providers for facility NPI: ${facilityNPI}`);
      } else if (facilitiesWithNPI.length > 1) {
        // Multiple facilities with same NPI - show providers from ALL matching facilities
        const facilityIds = facilitiesWithNPI.map(f => f.id);
        filtered = providers.filter(provider => 
          provider.affiliations?.some(affiliation => 
            facilityIds.includes(affiliation.facilityId)
          )
        );
        console.log(`⚠️ Multiple facilities with NPI ${facilityNPI}, showing providers from all (${filtered.length} total)`);
        console.log(`🏥 Facilities: ${facilitiesWithNPI.map(f => f.facilityName).join(', ')}`);
      }
    }

    setFilteredProviders(filtered);
  }, [providers, departmentName, facilityId, facilityNPI, facilities, departments]);

  useEffect(() => {
    filterProvidersByFacility();
  }, [filterProvidersByFacility]);

  // Convert providers to dropdown options format
  const providerOptions = filteredProviders.map(provider => ({
    label: `${provider.fullName}\nNPI: ${provider.npi}`,
    value: provider.value
  }));

  // Find the selected option
  const selectedOption = providerOptions.find(option => option.value === value) || null;

  const handleChange = (selectedOption) => {
    if (selectedOption) {
      onChange(selectedOption.value);
    } else {
      onChange('');
    }
  };

  return (
    <div style={{ position: 'relative', ...style }}>
      <Dropdown
        options={providerOptions}
        value={selectedOption}
        onChange={handleChange}
        placeholder={isLoading ? "Loading providers..." : placeholder}
        themeMode={themeMode}
        disabled={disabled || isLoading}
        isSearchable={true}
        width="100%"
        menuPlacement="auto"
      />
    </div>
  );
};

export default RenderingProviderSelection;